/**
 * Session 19b — OCPP 2.0.1 Device Model: the seeded standard Variable set.
 *
 * v2.0.1 replaces v1.6's flat key/value Configuration with a hierarchical
 * model: Variables live on Components, qualified by `(evseId, connectorId)`
 * and an attribute type (`Actual` / `Target` / `MinSet` / `MaxSet`), each
 * carrying characteristics (dataType, unit, …) and a mutability.
 *
 * Scope (per the Session 19b decision): a CURATED MINIMUM set — the prior
 * 10 Phase-7 keys plus a handful that matter for QA (MessageMaxReportLength
 * for GetReport pagination, ChargingStation identity, a SecurityCtrlr
 * basic). Every seeded Variable is UNQUALIFIED (evseId 0, connectorId 0)
 * on the `Actual` attribute, which keeps the flat projection
 * `"Component.Variable" → value` bijective so the existing flat-key
 * consumers (HeartbeatInterval timer, measurands, AuthorizationCacheEnabled,
 * …) keep working unchanged. The `(evseId, connectorId, attributeType)`
 * columns exist in storage for future per-EVSE/per-connector qualification;
 * the minimum seed simply doesn't use them yet.
 */
import { ConfigurationSection } from '@/domain/types';
import type { V201DataType } from '@/protocol/v201/actions/notifyReport';

/** Sentinel for "this Variable is not EVSE/connector qualified". */
export const UNQUALIFIED = 0;
export const DEFAULT_ATTRIBUTE_TYPE = 'Actual';

export type V201Mutability = 'ReadOnly' | 'ReadWrite' | 'WriteOnly';

/**
 * One standard Variable definition. `value` is the seeded `Actual` value.
 * `evseId` / `connectorId` default to UNQUALIFIED. The flat projection key
 * is `"${component}.${variable}"` (only valid while unqualified — see the
 * module header).
 */
export interface V201VariableDef {
  readonly component: string;
  readonly variable: string;
  readonly evseId: number;
  readonly connectorId: number;
  readonly attributeType: string;
  readonly value: string;
  readonly mutability: V201Mutability;
  readonly dataType: V201DataType;
  readonly unit?: string;
  /** True when a SetVariables write should reply RebootRequired (vs Accepted). */
  readonly rebootRequired: boolean;
  /** UI grouping bucket (mirrors the v1.6 ConfigurationSection taxonomy). */
  readonly section: ConfigurationSection;
}

function v(
  component: string,
  variable: string,
  value: string,
  mutability: V201Mutability,
  dataType: V201DataType,
  section: ConfigurationSection,
  opts: { unit?: string; rebootRequired?: boolean; evseId?: number; connectorId?: number } = {},
): V201VariableDef {
  return {
    component,
    variable,
    evseId: opts.evseId ?? UNQUALIFIED,
    connectorId: opts.connectorId ?? UNQUALIFIED,
    attributeType: DEFAULT_ATTRIBUTE_TYPE,
    value,
    mutability,
    dataType,
    ...(opts.unit !== undefined ? { unit: opts.unit } : {}),
    rebootRequired: opts.rebootRequired ?? false,
    section,
  };
}

const RW = 'ReadWrite' as const;
const RO = 'ReadOnly' as const;

/**
 * The curated minimum Device Model. Flat-key equivalents of the Phase-7
 * set are preserved 1:1 so a v2.0.1 station upgraded in place keeps the
 * same `Component.Variable` projection.
 */
export const STANDARD_VARIABLES_V201: readonly V201VariableDef[] = [
  // OCPPCommCtrlr
  v('OCPPCommCtrlr', 'HeartbeatInterval', '300', RW, 'integer', ConfigurationSection.Core, { unit: 's' }),
  v('OCPPCommCtrlr', 'WebSocketPingInterval', '0', RW, 'integer', ConfigurationSection.Core, { unit: 's' }),
  v('OCPPCommCtrlr', 'MessageTimeout', '30', RW, 'integer', ConfigurationSection.Core, { unit: 's' }),
  // Session 19b — controls NotifyReport pagination (GetReport/GetBaseReport).
  v('OCPPCommCtrlr', 'MessageMaxReportLength', '1024', RW, 'integer', ConfigurationSection.Core),
  // RebootRequired demonstrator (matches the spec's network-config family).
  v('OCPPCommCtrlr', 'NetworkConfigurationPriority', '0', RW, 'integer', ConfigurationSection.Core, {
    rebootRequired: true,
  }),
  // DeviceDataCtrlr
  v('DeviceDataCtrlr', 'NumberOfConnectors', '3', RO, 'integer', ConfigurationSection.Core),
  // SampledDataCtrlr
  v('SampledDataCtrlr', 'TxStartedMeasurands', 'Energy.Active.Import.Register', RW, 'MemberList', ConfigurationSection.Core),
  v(
    'SampledDataCtrlr',
    'TxUpdatedMeasurands',
    'Energy.Active.Import.Register,Power.Active.Import,SoC',
    RW,
    'MemberList',
    ConfigurationSection.Core,
  ),
  v('SampledDataCtrlr', 'TxEndedMeasurands', 'Energy.Active.Import.Register', RW, 'MemberList', ConfigurationSection.Core),
  v('SampledDataCtrlr', 'TxUpdatedInterval', '60', RW, 'integer', ConfigurationSection.Core, { unit: 's' }),
  // AuthCtrlr
  v('AuthCtrlr', 'AuthorizeRemoteStart', 'true', RW, 'boolean', ConfigurationSection.Auth),
  // AuthCacheCtrlr
  v('AuthCacheCtrlr', 'Enabled', 'false', RW, 'boolean', ConfigurationSection.Auth),
  // LocalAuthListCtrlr — surfaces the SPEC-001 list config in the Device Model.
  v('LocalAuthListCtrlr', 'Enabled', 'false', RW, 'boolean', ConfigurationSection.Auth),
  // SmartChargingCtrlr (Session 19c) — surfaces the smart-charging config.
  v('SmartChargingCtrlr', 'Enabled', 'true', RW, 'boolean', ConfigurationSection.SmartCharging),
  v('SmartChargingCtrlr', 'LimitChangeSignificance', '5', RW, 'decimal', ConfigurationSection.SmartCharging, {
    unit: 'percent',
  }),
  v(
    'SmartChargingCtrlr',
    'ChargingScheduleChargingRateUnit',
    'A,W',
    RO,
    'MemberList',
    ConfigurationSection.SmartCharging,
  ),
  v('SmartChargingCtrlr', 'PeriodsPerSchedule', '100', RO, 'integer', ConfigurationSection.SmartCharging),
  v(
    'SmartChargingCtrlr',
    'ExternalControlSignalsEnabled',
    'false',
    RW,
    'boolean',
    ConfigurationSection.SmartCharging,
  ),
  // DisplayMessageCtrlr (Session 19d).
  v('DisplayMessageCtrlr', 'DisplayMessages', '10', RO, 'integer', ConfigurationSection.Custom),
  v('DisplayMessageCtrlr', 'PersonalMessageSize', '1024', RO, 'integer', ConfigurationSection.Custom),
  // SecurityCtrlr basic (read-only identity).
  v('SecurityCtrlr', 'OrganizationName', 'VirtualCharger', RW, 'string', ConfigurationSection.Custom),
  // ChargingStation identity (read-only).
  v('ChargingStation', 'Model', 'sim', RO, 'string', ConfigurationSection.Custom),
  v('ChargingStation', 'VendorName', 'VirtualCharger', RO, 'string', ConfigurationSection.Custom),
];

/** Flat projection key `"Component.Variable"` for an unqualified Actual variable. */
export function flatKeyFor(def: { component: string; variable: string }): string {
  return `${def.component}.${def.variable}`;
}
